import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  useRef,
} from "react";
import {
  ThemeConfig,
  ThemeSettings,
  DEFAULT_THEME,
  PRESET_THEMES,
  SEOConfig,
} from "@/types/theme";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/contexts/PermissionContext";
import { useTenant } from "@/contexts/TenantContext";
import { fixThemeForegrounds } from "@/utils/themeFixer";
import { getCurrentTenantId } from "@/utils/tenantUtils";
import {
  getContrastingTextColor,
  generateHoverColor,
  getColorContrast,
} from "@/utils/themeUtils";
import { applySEOSettings } from "@/utils/seo";
import { applyDarkModeToTheme } from "@/utils/darkModeUtils";
import { completeThemeColors } from "@/utils/tenantThemeLoader";
import { useToast } from "@/components/ui/use-toast";

// Helper function to check if current route is unauthenticated
const isUnauthenticatedRoute = (): boolean => {
  const unauthenticatedPaths = [
    "/login",
    "/forgot-password",
    "/reset-password",
    "/home",
    "/mobile-support",
    "/sign",
    "/ai-audit",
  ];
  const currentPath = window.location.pathname;
  return (
    unauthenticatedPaths.includes(currentPath) ||
    currentPath.startsWith("/book/") ||
    currentPath.startsWith("/booking-confirmation/") ||
    currentPath.startsWith("/booking-diagnostic/") ||
    currentPath.startsWith("/ai-audit/") ||
    // KNK-1.22: the invite page, opened by a rep with no login yet.
    currentPath === "/join" ||
    currentPath.startsWith("/join/")
  );
};

// Helper function to get adaptive sidebar color (copied from ThemeTab)
const getAdaptiveSidebarColor = (
  primary: string,
  background: string
): string => {
  const isLight = getColorContrast(background) === "light";

  if (isLight) {
    // For light backgrounds, create a dark sidebar that complements the primary color
    const primaryIsDark = getColorContrast(primary) === "dark";

    if (primaryIsDark) {
      // If primary is dark, use it as the sidebar color
      return primary;
    } else {
      // If primary is light, use a dark neutral color
      return "#1f2937"; // Dark gray that works well with most themes
    }
  } else {
    // For dark backgrounds, use a lighter sidebar
    const primaryIsLight = getColorContrast(primary) === "light";

    if (primaryIsLight) {
      // If primary is light, use it as the sidebar color
      return primary;
    } else {
      // If primary is dark, use a light neutral color
      return "#f3f4f6"; // Light gray that works well with most themes
    }
  }
};

interface ThemeContextType {
  currentTheme: ThemeConfig;
  themeSettings: ThemeSettings;
  setTheme: (themeId: string) => Promise<void>;
  updateCustomLogo: (logoUrl: string) => Promise<void>;
  /** The uploaded dark-mode logo. Only used when logoDarkTreatment is 'custom'. */
  updateCustomLogoDark: (logoUrl: string) => Promise<void>;
  /** How the logo is handled in dark mode: unchanged, inverted, or a separate upload. */
  updateLogoDarkTreatment: (treatment: 'same' | 'invert' | 'custom') => Promise<void>;
  updateCustomFavicon: (faviconUrl: string) => Promise<void>;
  updateCustomSEO: (seoSettings: SEOConfig) => Promise<void>;
  createCustomTheme: (
    theme: Omit<ThemeConfig, "id" | "createdAt">
  ) => Promise<void>;
  updateCustomTheme: (
    themeId: string,
    updates: Partial<ThemeConfig>
  ) => Promise<void>;
  deleteCustomTheme: (themeId: string) => Promise<void>;
  updateSidebarColor: (color: string) => Promise<void>;
  applyTheme: (theme: ThemeConfig) => void;
  toggleDarkMode: () => Promise<void>;
  isDarkMode: boolean;
  isLoading: boolean;
  canManageTheme: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [themeSettings, setThemeSettings] = useState<ThemeSettings>({
    currentTheme: DEFAULT_THEME.id,
    themes: PRESET_THEMES,
    customThemes: [],
    sidebarColorOverride: null,
    darkMode: false, // This will be overridden by user preferences
  });
  const [userDarkMode, setUserDarkMode] = useState(false); // User-specific dark mode preference
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [canManageTheme, setCanManageTheme] = useState(false);
  const { toast } = useToast();
  
  // Refs to track current values for stable history API override
  const currentThemeRef = useRef(themeSettings.currentTheme);
  const isLoadingRef = useRef(isLoading);
  const userDarkModeRef = useRef(userDarkMode);
  
  const {
    hasPermission,
    loading: permissionLoading,
    permissions,
  } = usePermissions();
  const { session, loading: authLoading, isImpersonating } = useAuth();
  const { currentTenant } = useTenant();
  
  // Initialize session ref after session is declared
  const sessionRef = useRef(session);
  
  // Update refs when values change
  useEffect(() => {
    currentThemeRef.current = themeSettings.currentTheme;
  }, [themeSettings.currentTheme]);
  
  useEffect(() => {
    isLoadingRef.current = isLoading;
  }, [isLoading]);
  
  useEffect(() => {
    userDarkModeRef.current = userDarkMode;
  }, [userDarkMode]);
  
  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  // Watch for permission changes and update canManageTheme
  useEffect(() => {
    if (!permissionLoading) {
      const hasThemePermissions =
        hasPermission("settings.admin") ||
        hasPermission("manage_roles") ||
        hasPermission("manage_users") ||
        hasPermission("system.override");
      setCanManageTheme(hasThemePermissions);
    } else {
      setCanManageTheme(false);
    }
  }, [permissionLoading, hasPermission, permissions]);

  const baseTheme =
    [...themeSettings.themes, ...themeSettings.customThemes].find(
      (t) => t.id === themeSettings.currentTheme
    ) || DEFAULT_THEME;

  // Apply custom logo and favicon to base theme
  let themeWithCustomAssets = baseTheme;
  if (themeSettings.customLogo) {
    themeWithCustomAssets = {
      ...themeWithCustomAssets,
      logo: themeSettings.customLogo,
    };
  }
  if (themeSettings.customFavicon) {
    themeWithCustomAssets = {
      ...themeWithCustomAssets,
      favicon: themeSettings.customFavicon,
    };
  }

  // Apply sidebar color override if it exists and the theme is not custom
  const currentTheme = fixThemeForegrounds(
    themeSettings.sidebarColorOverride && !baseTheme.isCustom
      ? {
          ...themeWithCustomAssets,
          colors: {
            ...themeWithCustomAssets.colors,
            sidebarColor: themeSettings.sidebarColorOverride,
            sidebarTextColor: getContrastingTextColor(
              themeSettings.sidebarColorOverride
            ),
            sidebarHoverBgColor: generateHoverColor(
              themeSettings.sidebarColorOverride,
              0.15
            ),
            sidebarHoverTextColor: getContrastingTextColor(
              generateHoverColor(themeSettings.sidebarColorOverride, 0.15)
            ),
          },
        }
      : themeWithCustomAssets
  );

  // Apply theme to CSS custom properties
  const applyTheme = (theme: ThemeConfig) => {
    /*
     * COMPLETE THE PALETTE FIRST. `setProperty` only ever ADDS, so any variable a theme does
     * not carry keeps whatever the last theme (or index.css) left behind. `completeThemeColors`
     * exists for exactly this and documents both bugs it closes — but it was only wired into
     * tenantThemeLoader's own path, not this one, which is the path the app uses when signed
     * in. Read its doc comment: of the eight tenants with an active theme, Solerro's carries 12
     * of 27 colors, so 15 variables came from somewhere else entirely.
     */
    const completeTheme: ThemeConfig = {
      ...theme,
      colors: completeThemeColors(theme.colors),
    };

    // Foregrounds after completion, so contrast is derived from the color that will actually
    // render rather than from a missing one.
    let fixedTheme = fixThemeForegrounds(completeTheme);

    /*
     * DARK MODE LAST. `applyDarkModeToTheme` DERIVES dark colors from the palette it is given,
     * so anything that adjusts the palette has to happen before this point — and this function
     * is the single place dark mode is applied. It used to also be applied by the route-change
     * handler below, which then called in here and applied it a second time.
     */
    if (userDarkMode && session && !isUnauthenticatedRoute()) {
      fixedTheme = {
        ...fixedTheme,
        colors: applyDarkModeToTheme(fixedTheme.colors, true),
      };
    }

    const root = document.documentElement;

    // Apply dark mode class for any remaining Tailwind dark: utilities
    // Only apply dark mode if user's dark mode is enabled and user is authenticated and not on unauthenticated route
    if (userDarkMode && session && !isUnauthenticatedRoute()) {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }

    // Apply color variables
    Object.entries(fixedTheme.colors).forEach(([key, value]) => {
      root.style.setProperty(`--${key}`, value);
    });

    // Apply logo and favicon
    if (fixedTheme.logo) {
      root.style.setProperty("--logo-url", `url(${fixedTheme.logo})`);
    }
    if (fixedTheme.favicon) {
      root.style.setProperty("--favicon-url", `url(${fixedTheme.favicon})`);
    }

    // Apply sidebar colors
    if (fixedTheme.colors.sidebarColor) {
      root.style.setProperty("--sidebar-color", fixedTheme.colors.sidebarColor);
    }
    if (fixedTheme.colors.sidebarTextColor) {
      root.style.setProperty(
        "--sidebar-text-color",
        fixedTheme.colors.sidebarTextColor
      );
    }
    if (fixedTheme.colors.sidebarHoverBgColor) {
      root.style.setProperty(
        "--sidebar-hover-bg-color",
        fixedTheme.colors.sidebarHoverBgColor
      );
    }
    if (fixedTheme.colors.sidebarHoverTextColor) {
      root.style.setProperty(
        "--sidebar-hover-text-color",
        fixedTheme.colors.sidebarHoverTextColor
      );
    }

    // Update favicon in head
    const faviconLink = document.querySelector(
      'link[rel="icon"]'
    ) as HTMLLinkElement;
    if (faviconLink && fixedTheme.favicon) {
      faviconLink.href = fixedTheme.favicon;
    }

    // SEO will be applied after theme is fully loaded to prevent flickering
  };

  // Load user preferences from database
  const loadUserPreferences = async () => {
    try {
      if (!session) {
        console.warn("No session found when loading user preferences");
        return;
      }

      const { data, error } = await supabase
        .from("user_preferences")
        .select("dark_mode")
        .eq("user_id", session.user.id)
        .single();

      if (error && error.code !== "PGRST116") {
        // PGRST116 = no rows returned
        console.error("Error loading user preferences:", error);
        return;
      }

      if (data) {
        setUserDarkMode(data.dark_mode || false);
      }
    } catch (error) {
      console.error("Error loading user preferences:", error);
    }
  };

  // Save user preferences to database
  const saveUserPreferences = async (darkMode: boolean) => {
    try {
      if (!session) {
        console.warn("No session found when saving user preferences");
        return;
      }

      const { error } = await supabase.from("user_preferences").upsert(
        {
          user_id: session.user.id,
          dark_mode: darkMode,
          updated_at: new Date().toISOString(),
        },
        {
          onConflict: "user_id",
        }
      );

      if (error) {
        console.error("Error saving user preferences:", error);
      }
    } catch (error) {
      console.error("Error saving user preferences:", error);
    }
  };

  // Load theme settings from database
  const loadThemeSettings = async () => {
    try {
      // Skip theme loading if modal is open to prevent interference
      if ((window as any).__JOULES_MODAL_OPEN) {
        return;
      }
      
      // Use the session from AuthContext instead of fetching it again
      if (!session) {
        console.warn("No session found when loading theme settings");
        setIsLoading(false);
        return;
      }

      // Force fresh data by adding a timestamp parameter (prevents caching)
      const { data, error } = await supabase.rpc("get_theme_setting");

      if (error) {
        console.error("Error loading theme settings:", error);
        console.error("Error details:", {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint,
        });
      }

      if (data && data.current_theme) {
        // Parse custom themes from JSON
        let customThemes = [];
        if (data.custom_themes && Array.isArray(data.custom_themes)) {
          customThemes = data.custom_themes;
        }

        const newSettings = {
          currentTheme: data.current_theme || DEFAULT_THEME.id,
          customLogo: data.custom_logo,
          // Undefined until the 20260811_brand_dark_logo migration runs, which is why
          // resolveAppLogo treats absent as "use the light logo".
          customLogoDark: data.custom_logo_dark,
          logoDarkTreatment: data.logo_dark_treatment,
          customFavicon: data.custom_favicon,
          customSEO: data.custom_seo || null,
          themes: PRESET_THEMES,
          customThemes: customThemes,
          sidebarColorOverride: data.sidebar_color_override || null,
          darkMode: false, // No longer used - dark mode is now user-specific
        };
        setThemeSettings(newSettings);

        // Apply the theme immediately after loading settings
        let themeToApply =
          [...PRESET_THEMES, ...customThemes].find(
            (t) => t.id === newSettings.currentTheme
          ) || DEFAULT_THEME;

        // Apply custom logo and favicon if they exist

        if (newSettings.customLogo) {
          themeToApply = {
            ...themeToApply,
            logo: newSettings.customLogo,
          };
        }
        if (newSettings.customFavicon) {
          themeToApply = {
            ...themeToApply,
            favicon: newSettings.customFavicon,
          };
        }

        // Apply sidebar color override if it exists and the theme is not custom
        if (newSettings.sidebarColorOverride && !themeToApply.isCustom) {
          themeToApply = {
            ...themeToApply,
            colors: {
              ...themeToApply.colors,
              sidebarColor: newSettings.sidebarColorOverride,
              sidebarTextColor: getContrastingTextColor(
                newSettings.sidebarColorOverride
              ),
              sidebarHoverBgColor: generateHoverColor(
                newSettings.sidebarColorOverride,
                0.15
              ),
              sidebarHoverTextColor: getContrastingTextColor(
                generateHoverColor(newSettings.sidebarColorOverride, 0.15)
              ),
            },
          };
        }


        applyTheme(themeToApply);

        // Apply SEO settings after theme is applied - only if tenant context is loaded
        if (currentTenant !== undefined) {
          if (newSettings.customSEO) {
            applySEOSettings(
              newSettings.customSEO,
              currentTenant?.company_name
            );
          } else if (themeToApply.seo) {
            applySEOSettings(themeToApply.seo, currentTenant?.company_name);
          } else {
            applySEOSettings(null, currentTenant?.company_name);
          }
        }
      } else {
        // No saved settings found, apply default theme
        applyTheme(DEFAULT_THEME);

        // Apply tenant-aware fallback SEO - only if tenant context is loaded
        if (currentTenant !== undefined) {
          if (DEFAULT_THEME.seo) {
            applySEOSettings(DEFAULT_THEME.seo, currentTenant?.company_name);
          } else {
            applySEOSettings(null, currentTenant?.company_name);
          }
        }
      }
    } catch (error) {
      console.error("Error loading theme settings:", error);
      // Apply default theme on error
      applyTheme(DEFAULT_THEME);

      // Apply tenant-aware fallback SEO on error - only if tenant context is loaded
      if (currentTenant !== undefined) {
        if (DEFAULT_THEME.seo) {
          applySEOSettings(DEFAULT_THEME.seo, currentTenant?.company_name);
        } else {
          applySEOSettings(null, currentTenant?.company_name);
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Save theme settings to database
  const saveThemeSettings = async (settings: Partial<ThemeSettings>) => {
    try {
      // Use the session from AuthContext instead of fetching it again
      if (!session) {
        console.warn("No session found when saving theme settings");
        return;
      }

      setIsSaving(true);

      // Try using the database function first (excluding dark_mode parameter - now user-specific)
      let { data, error } = await supabase.rpc("upsert_theme_setting", {
        p_current_theme: settings.currentTheme,
        p_custom_logo: settings.customLogo,
        p_custom_logo_dark: settings.customLogoDark ?? null,
        p_logo_dark_treatment: settings.logoDarkTreatment ?? 'same',
        p_custom_favicon: settings.customFavicon,
        p_custom_themes: settings.customThemes || [],
        p_sidebar_color_override: settings.sidebarColorOverride || null,
        p_custom_seo: settings.customSEO || null,
      });

      /**
       * ⚠️ THE FALLBACK NOW RUNS ON ANY RPC ERROR, NOT ONLY FUNCTION-SHAPED ONES.
       *
       * It used to be gated on `PGRST203 | 42883 | 42703` or a message containing
       * "function" / "does not exist". That list was written for the overload conflict and
       * silently excluded everything else, which is how the 2026-08-18 branding report
       * happened: `upsert_theme_setting` gated on `profiles.is_admin`, 52% of active users
       * have no `profiles` row, and the resulting P0001 matched none of those conditions.
       * The fallback was skipped, the error fell through to a bare `console.error`, and the
       * optimistically applied theme made it look saved until the next reload.
       *
       * Matching on error SHAPE was the mistake. Trying the direct write on any failure
       * costs one request in the rare error case and cannot widen access: the table's RLS
       * evaluates the same predicate the function does, so a caller who is genuinely not
       * allowed is refused again here and told so below.
       */
      if (error) {
        console.warn(
          `upsert_theme_setting failed (${error.code}: ${error.message}), falling back to direct table access`
        );

        // Get tenant ID from TenantContext instead of cached utility to prevent cross-tenant contamination
        const tenantId = currentTenant?.tenant_id;
        if (!tenantId) {
          console.error("No tenant ID available for saving theme settings");
          toast({
            title: "Branding not saved",
            description:
              "The account being edited could not be determined. Reload the page and try again.",
            variant: "destructive",
          });
          return;
        }

        const { data: fallbackData, error: fallbackError } = await supabase
          .from("tenant_theme_settings")
          .upsert(
            {
              tenant_id: tenantId,
              current_theme: settings.currentTheme,
              custom_logo: settings.customLogo,
              custom_logo_dark: settings.customLogoDark ?? null,
              logo_dark_treatment: settings.logoDarkTreatment ?? 'same',
              custom_favicon: settings.customFavicon,
              custom_themes: settings.customThemes || [],
              sidebar_color_override: settings.sidebarColorOverride || null,
              custom_seo: settings.customSEO || null,
              updated_at: new Date().toISOString(),
            },
            {
              onConflict: "tenant_id",
            }
          )
          .select()
          .single();

        data = fallbackData;
        error = fallbackError;
      }

      /**
       * ⚠️ A FAILED SAVE MUST SAY SO, AND MUST NOT LEAVE THE NEW THEME ON SCREEN.
       *
       * This branch used to be a `console.error` and an empty `else`. The caller had
       * already applied the theme optimistically to local state, so a hard refusal from the
       * database looked exactly like a success until the user reloaded and watched their
       * branding revert. That is the whole "it isn't sticking" report: not a lost write, a
       * write that was refused and never mentioned.
       *
       * `loadThemeSettings()` puts the UI back onto what the server actually holds, so what
       * is on screen after this function returns is true either way.
       */
      if (error) {
        console.error("Error saving theme settings:", error);
        console.error("Error details:", {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint,
        });

        toast({
          title: "Branding not saved",
          description:
            // 42501 is the server's own authority refusal and its message is written for
            // this user, so it is shown as-is rather than replaced with a generic line.
            error.code === "42501" && error.message
              ? error.message
              : "The change could not be saved and has been rolled back. Please try again, and contact support if it keeps happening.",
          variant: "destructive",
        });

        await loadThemeSettings();
      }
    } catch (error) {
      console.error("Error saving theme settings:", error);
      toast({
        title: "Branding not saved",
        description:
          error instanceof Error
            ? error.message
            : "The change could not be saved and has been rolled back.",
        variant: "destructive",
      });
      await loadThemeSettings();
    } finally {
      setIsSaving(false);
    }
  };

  // Set theme
  const setTheme = async (themeId: string) => {
    if (!canManageTheme) {
      console.warn("Only admins can change theme settings");
      return;
    }

    // Look for theme in both preset themes and custom themes
    const theme = [...themeSettings.themes, ...themeSettings.customThemes].find(
      (t) => t.id === themeId
    );
    if (!theme) {
      console.warn(`Theme with id ${themeId} not found`);
      return;
    }

    // Clear sidebar color override when switching themes (for preset themes)
    const newSettings = {
      ...themeSettings,
      currentTheme: themeId,
      sidebarColorOverride: theme.isCustom
        ? themeSettings.sidebarColorOverride
        : null,
    };
    setThemeSettings(newSettings);
    applyTheme(theme);
    await saveThemeSettings(newSettings);
  };

  // Update custom logo
  const updateCustomLogo = async (logoUrl: string) => {
    if (!canManageTheme) {
      console.warn("Only admins can update logo settings");
      return;
    }

    const newSettings = { ...themeSettings, customLogo: logoUrl };
    setThemeSettings(newSettings);

    // Apply custom logo to current theme
    const themeWithCustomLogo = { ...currentTheme, logo: logoUrl };
    applyTheme(themeWithCustomLogo);

    await saveThemeSettings(newSettings);
  };

  /**
   * The dark-mode logo. Deliberately NOT folded into `currentTheme.logo`: proposals, share
   * links and the customer portal all read that and all render on a light ground, so the dark
   * variant must stay out of it. Only Logo.tsx (app chrome) consults this.
   */
  const updateCustomLogoDark = async (logoUrl: string) => {
    if (!canManageTheme) {
      console.warn("Only admins can update logo settings");
      return;
    }

    const newSettings = { ...themeSettings, customLogoDark: logoUrl };
    setThemeSettings(newSettings);
    await saveThemeSettings(newSettings);
  };

  /**
   * Which of the three dark-logo treatments applies. Stored rather than derived, so choosing
   * 'invert' and then uploading an image does not leave the winner ambiguous.
   */
  const updateLogoDarkTreatment = async (
    treatment: 'same' | 'invert' | 'custom'
  ) => {
    if (!canManageTheme) {
      console.warn("Only admins can update logo settings");
      return;
    }

    const newSettings = { ...themeSettings, logoDarkTreatment: treatment };
    setThemeSettings(newSettings);
    await saveThemeSettings(newSettings);
  };

  // Update custom favicon
  const updateCustomFavicon = async (faviconUrl: string) => {
    if (!canManageTheme) {
      console.warn("Only admins can update favicon settings");
      return;
    }

    const newSettings = { ...themeSettings, customFavicon: faviconUrl };
    setThemeSettings(newSettings);

    // Apply custom favicon to current theme
    const themeWithCustomFavicon = { ...currentTheme, favicon: faviconUrl };
    applyTheme(themeWithCustomFavicon);

    await saveThemeSettings(newSettings);
  };

  // Update custom SEO settings
  const updateCustomSEO = async (seoSettings: SEOConfig) => {
    if (!canManageTheme) {
      console.warn("Only admins can update SEO settings");
      return;
    }

    const newSettings = { ...themeSettings, customSEO: seoSettings };
    setThemeSettings(newSettings);

    // Apply SEO settings immediately
    applySEOSettings(seoSettings, currentTenant?.company_name);

    await saveThemeSettings(newSettings);
  };

  // Create custom theme
  const createCustomTheme = async (
    theme: Omit<ThemeConfig, "id" | "createdAt">
  ) => {
    if (!canManageTheme) {
      console.warn("Only admins can create custom themes");
      return;
    }

    const newTheme: ThemeConfig = {
      ...theme,
      id: `custom-${Date.now()}`,
      isCustom: true,
      createdAt: new Date().toISOString(),
    };

    const newSettings = {
      ...themeSettings,
      customThemes: [...themeSettings.customThemes, newTheme],
    };
    setThemeSettings(newSettings);
    await saveThemeSettings(newSettings);
  };

  // Update custom theme
  const updateCustomTheme = async (
    themeId: string,
    updates: Partial<ThemeConfig>
  ) => {
    if (!canManageTheme) {
      console.warn("Only admins can update custom themes");
      return;
    }

    const existingTheme = themeSettings.customThemes.find(
      (t) => t.id === themeId
    );
    if (!existingTheme) {
      console.warn(`Custom theme with id ${themeId} not found`);
      return;
    }

    const updatedTheme: ThemeConfig = { ...existingTheme, ...updates };
    const newSettings: ThemeSettings = {
      ...themeSettings,
      customThemes: themeSettings.customThemes.map((theme) =>
        theme.id === themeId ? updatedTheme : theme
      ),
    };
    setThemeSettings(newSettings);

    // If we're updating the currently active theme, apply it immediately
    if (themeSettings.currentTheme === themeId) {
      applyTheme(updatedTheme);
    }

    await saveThemeSettings(newSettings);
  };

  // Delete custom theme
  const deleteCustomTheme = async (themeId: string) => {
    if (!canManageTheme) {
      console.warn("Only admins can delete custom themes");
      return;
    }

    const newSettings = {
      ...themeSettings,
      customThemes: themeSettings.customThemes.filter(
        (theme) => theme.id !== themeId
      ),
    };
    setThemeSettings(newSettings);
    await saveThemeSettings(newSettings);
  };

  // Update sidebar color for any theme (preset or custom)
  const updateSidebarColor = async (color: string) => {
    if (!canManageTheme) {
      console.warn("Only admins can update sidebar colors");
      return;
    }

    // Check if this is the default theme-adaptive color (no override needed)
    const baseTheme =
      [...themeSettings.themes, ...themeSettings.customThemes].find(
        (t) => t.id === themeSettings.currentTheme
      ) || DEFAULT_THEME;
    const defaultAdaptiveColor = getAdaptiveSidebarColor(
      baseTheme.colors.primary,
      baseTheme.colors.background
    );

    // Create a modified version of the current theme with the new sidebar color
    const updatedTheme = {
      ...currentTheme,
      colors: {
        ...currentTheme.colors,
        sidebarColor: color,
        sidebarTextColor: getContrastingTextColor(color),
        sidebarHoverBgColor: generateHoverColor(color, 0.15),
        sidebarHoverTextColor: getContrastingTextColor(
          generateHoverColor(color, 0.15)
        ),
      },
    };

    // Apply the updated theme immediately
    applyTheme(updatedTheme);

    // If it's a custom theme, update it in the database
    if (currentTheme.isCustom) {
      await updateCustomTheme(currentTheme.id, updatedTheme);
    } else {
      // For preset themes, save the sidebar color override (or clear it if using default)
      const newSettings = {
        ...themeSettings,
        currentTheme: currentTheme.id,
        sidebarColorOverride: color === defaultAdaptiveColor ? null : color,
      };
      setThemeSettings(newSettings);
      await saveThemeSettings(newSettings);
    }
  };

  // Toggle dark mode (user-specific)
  const toggleDarkMode = async () => {
    const newDarkMode = !userDarkMode;
    setUserDarkMode(newDarkMode);

    // Apply the current theme with new dark mode setting
    applyTheme(currentTheme);

    // Don't save to database during impersonation to avoid tenant/permission issues
    // Dark mode will revert when impersonation ends, but works during the session
    if (!isImpersonating) {
      // Save to user preferences (not tenant settings)
      await saveUserPreferences(newDarkMode);
    }
  };

  // Load settings when session is available or tenant changes
  useEffect(() => {
    // Don't reload if modal is open
    if ((window as any).__JOULES_MODAL_OPEN) {
      return;
    }
    
    // Don't reload if we're currently saving to prevent overriding local state
    if (isSaving) {
      return;
    }

    // Don't reload if tenant is undefined (still loading)
    if (currentTenant === undefined) {
      return;
    }

    if (!authLoading && session) {
      // Check if we're on ai-audit routes - use domain tenant theme even for authenticated users
      const currentPath = window.location.pathname;
      const isAiAuditRoute = currentPath.startsWith('/ai-audit');
      
      if (isAiAuditRoute) {
        const initialDomainTenant = sessionStorage.getItem('initial_domain_tenant');
        if (initialDomainTenant) {
          try {
            const domainTenant = JSON.parse(initialDomainTenant);
            if (domainTenant?.theme) {
              
              // Create theme settings from domain tenant data
              const domainThemeSettings = {
                currentTheme: domainTenant.theme.currentTheme || DEFAULT_THEME.id,
                customLogo: domainTenant.theme.customLogo,
                customFavicon: domainTenant.theme.customFavicon,
                customSEO: domainTenant.theme.customSEO,
                themes: PRESET_THEMES,
                customThemes: domainTenant.theme.customThemes || [],
                sidebarColorOverride: domainTenant.theme.sidebarColorOverride,
                darkMode: false
              };
              
              setThemeSettings(domainThemeSettings);
              
              // Find the correct theme to apply
              let themeToApply = PRESET_THEMES.find(t => t.id === domainTenant.theme.currentTheme);
              
              // Check custom themes if not found in presets
              if (!themeToApply && domainTenant.theme.customThemes) {
                themeToApply = domainTenant.theme.customThemes.find((t: any) => t.id === domainTenant.theme.currentTheme);
              }
              
              // Fallback to default theme
              if (!themeToApply) {
                themeToApply = DEFAULT_THEME;
              }
              
              // Apply custom logo and favicon if available
              if (domainTenant.theme.customLogo) {
                themeToApply = { ...themeToApply, logo: domainTenant.theme.customLogo };
              }
              if (domainTenant.theme.customFavicon) {
                themeToApply = { ...themeToApply, favicon: domainTenant.theme.customFavicon };
              }
              
              applyTheme(themeToApply);
              
              setIsLoading(false);
              return;
            }
          } catch (e) {
            console.warn('Failed to parse initial domain tenant for authenticated user:', e);
          }
        }
      }
      
      loadThemeSettings();
      loadUserPreferences(); // Load user's dark mode preference
    } else if (!authLoading && !session) {
      // Check for pre-loaded domain tenant theme for unauthenticated users
      const initialDomainTenant = sessionStorage.getItem('initial_domain_tenant');
      if (initialDomainTenant) {
        try {
          const domainTenant = JSON.parse(initialDomainTenant);
          if (domainTenant?.theme) {
            // Apply the domain tenant theme properly instead of just preserving it
            
            // Create theme settings from domain tenant data
            const domainThemeSettings = {
              currentTheme: domainTenant.theme.currentTheme || DEFAULT_THEME.id,
              customLogo: domainTenant.theme.customLogo,
              customFavicon: domainTenant.theme.customFavicon,
              customSEO: domainTenant.theme.customSEO,
              themes: PRESET_THEMES,
              customThemes: domainTenant.theme.customThemes || [],
              sidebarColorOverride: domainTenant.theme.sidebarColorOverride,
              darkMode: false
            };
            
            setThemeSettings(domainThemeSettings);
            
            // Find the correct theme to apply
            let themeToApply = PRESET_THEMES.find(t => t.id === domainTenant.theme.currentTheme);
            
            // Check custom themes if not found in presets
            if (!themeToApply && domainTenant.theme.customThemes) {
              themeToApply = domainTenant.theme.customThemes.find((t: any) => t.id === domainTenant.theme.currentTheme);
            }
            
            // Fallback to default theme
            if (!themeToApply) {
              themeToApply = DEFAULT_THEME;
            }
            
            // Apply custom logo and favicon if available
            if (domainTenant.theme.customLogo) {
              themeToApply = { ...themeToApply, logo: domainTenant.theme.customLogo };
            }
            if (domainTenant.theme.customFavicon) {
              themeToApply = { ...themeToApply, favicon: domainTenant.theme.customFavicon };
            }
            
            applyTheme(themeToApply);
            
            setIsLoading(false);
            return;
          }
        } catch (e) {
          console.warn('Failed to parse initial domain tenant:', e);
          sessionStorage.removeItem('initial_domain_tenant');
        }
      }
      
      setIsLoading(false);
      applyTheme(DEFAULT_THEME);
    }
  }, [session, authLoading, isSaving, currentTenant]);

  // Apply theme when current theme or user dark mode changes
  useEffect(() => {
    // Skip theme changes if modal is open to prevent interference
    if ((window as any).__JOULES_MODAL_OPEN) {
      return;
    }
    
    if (!isLoading) {
      // Don't override domain theme for unauthenticated users
      if (!session) {
        const initialDomainTenant = sessionStorage.getItem('initial_domain_tenant');
        if (initialDomainTenant) {
          try {
            const domainTenant = JSON.parse(initialDomainTenant);
            if (domainTenant?.theme) {
              return;
            }
          } catch (e) {
            // Continue with normal theme application
          }
        }
      }
      
      applyTheme(currentTheme);
    }
  }, [currentTheme, isLoading, userDarkMode, session]);

  // Listen for route changes to reapply theme when navigating between authenticated/unauthenticated routes
  useEffect(() => {
    let isOverridden = false;
    
    const handleLocationChange = () => {
      // Skip theme changes if modal is open to prevent interference
      if ((window as any).__JOULES_MODAL_OPEN) {
        return;
      }
      
      // Only apply theme changes when navigating between authenticated/unauthenticated routes
      // Skip for regular navigation within the app to prevent theme flashing
      const isUnauthRoute = isUnauthenticatedRoute();
      const wasUnauthRoute = sessionStorage.getItem('lastRouteWasUnauth') === 'true';
      
      // Store current route type for next check
      sessionStorage.setItem('lastRouteWasUnauth', isUnauthRoute.toString());
      
      // Only proceed if transitioning between auth states
      if (isUnauthRoute === wasUnauthRoute) {
        return;
      }
      
      if (!isLoadingRef.current) {
        // Get the base theme
        const baseTheme = [...themeSettings.themes, ...themeSettings.customThemes].find(
          (t) => t.id === currentThemeRef.current
        ) || DEFAULT_THEME;
        
        // Apply custom logo and favicon to base theme if they exist
        let themeWithCustomAssets = baseTheme;
        if (themeSettings.customLogo) {
          themeWithCustomAssets = {
            ...themeWithCustomAssets,
            logo: themeSettings.customLogo,
          };
        }
        if (themeSettings.customFavicon) {
          themeWithCustomAssets = {
            ...themeWithCustomAssets,
            favicon: themeSettings.customFavicon,
          };
        }

        // Apply sidebar color override if it exists and the theme is not custom
        const finalTheme = themeSettings.sidebarColorOverride && !baseTheme.isCustom
          ? {
              ...themeWithCustomAssets,
              colors: {
                ...themeWithCustomAssets.colors,
                sidebarColor: themeSettings.sidebarColorOverride,
                sidebarTextColor: getContrastingTextColor(
                  themeSettings.sidebarColorOverride
                ),
                sidebarHoverBgColor: generateHoverColor(
                  themeSettings.sidebarColorOverride,
                  0.15
                ),
                sidebarHoverTextColor: getContrastingTextColor(
                  generateHoverColor(themeSettings.sidebarColorOverride, 0.15)
                ),
              },
            }
          : themeWithCustomAssets;

        /*
         * `applyTheme` owns dark mode, and it is the last step there. This handler used to
         * derive the dark palette itself and then hand the result to `applyTheme`, which
         * derived it again — a double application that survived only because
         * `applyDarkModeToTheme` is near-idempotent on already-dark colors. Deriving a
         * palette twice is not something to rely on staying harmless.
         */
        applyTheme(fixThemeForegrounds(finalTheme));
      }
    };

    // Only set up listeners if not already done
    if (!isOverridden) {
      // Listen for both popstate (browser back/forward) and pushstate/replacestate (programmatic navigation)
      window.addEventListener("popstate", handleLocationChange);

      // Store original functions to avoid multiple overrides
      const originalPushState = history.pushState;
      const originalReplaceState = history.replaceState;

      history.pushState = function (...args) {
        originalPushState.apply(this, args);
        setTimeout(handleLocationChange, 0); // Use setTimeout to avoid immediate execution
      };

      history.replaceState = function (...args) {
        originalReplaceState.apply(this, args);
        setTimeout(handleLocationChange, 0);
      };

      isOverridden = true;

      return () => {
        window.removeEventListener("popstate", handleLocationChange);
        // Only restore if we actually overrode them
        if (isOverridden) {
          history.pushState = originalPushState;
          history.replaceState = originalReplaceState;
        }
      };
    }
  }, []); // Keep empty to prevent multiple overrides

  // Apply SEO when tenant becomes available and theme is loaded
  useEffect(() => {
    if (!isLoading && currentTenant !== undefined) {
      // Apply SEO in priority order: custom SEO > theme SEO > tenant fallback
      if (themeSettings.customSEO) {
        applySEOSettings(themeSettings.customSEO, currentTenant?.company_name);
      } else if (currentTheme.seo) {
        applySEOSettings(currentTheme.seo, currentTenant?.company_name);
      } else {
        applySEOSettings(null, currentTenant?.company_name);
      }
    }
  }, [currentTenant, isLoading, themeSettings.customSEO, currentTheme.seo]);

  const value: ThemeContextType = useMemo(() => ({
    currentTheme,
    themeSettings,
    setTheme,
    updateCustomLogo,
    updateCustomLogoDark,
    updateLogoDarkTreatment,
    updateCustomFavicon,
    updateCustomSEO,
    createCustomTheme,
    updateCustomTheme,
    deleteCustomTheme,
    updateSidebarColor,
    applyTheme,
    toggleDarkMode,
    isDarkMode: userDarkMode,
    isLoading,
    canManageTheme,
  }), [currentTheme, themeSettings, setTheme, updateCustomLogo, updateCustomLogoDark, updateLogoDarkTreatment, updateCustomFavicon, updateCustomSEO, createCustomTheme, updateCustomTheme, deleteCustomTheme, updateSidebarColor, applyTheme, toggleDarkMode, userDarkMode, isLoading, canManageTheme]);

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};
